import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import { EyebrowLabel } from "../../UIToolkit/molecules/EyebrowLabel";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HolidayGroupChip } from "./HolidayGroupChip";
import type { Holiday, HolidayGroup } from "./holidayApi";
import { formatHolidayDate } from "./holidayFormat";

type Props = { holiday: Holiday; groups: readonly HolidayGroup[] };

/** One holiday in a popup: its name, date and shifts, and the group that works it. */
export const HolidaySummaryCard: FC<Props> = ({ holiday, groups }) => {
	const { t } = useTranslation();
	return (
		<Box
			data-testid={`holiday-summary-${holiday.id}`}
			sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5, border: `1px solid ${NEUTRAL[200]}`, borderRadius: 1, backgroundColor: NEUTRAL[50] }}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, flex: 1 }}>
				<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{holiday.name}</Typography>
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.25 }}>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
						{formatHolidayDate(holiday.date)}
					</Typography>
					<Box component="span" aria-hidden sx={{ color: INK.MUTED }}>
						·
					</Box>
					<HolidayDayPartLabel holiday={holiday} />
				</Box>
			</Box>
			{holiday.selectedGroupId !== null && (
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
					<EyebrowLabel>{t("holidays.list.column.works")}</EyebrowLabel>
					<HolidayGroupChip groups={groups} groupId={holiday.selectedGroupId} size="medium" />
				</Box>
			)}
		</Box>
	);
};
